/** Maps a Firebase Auth error to a friendly, customer-facing message. */
export function describeAuthError(error: unknown): string {
  // Surface the raw error to the console so devs can see the underlying
  // Firebase code/message even when the UI shows the friendly summary.
  if (typeof window !== "undefined" && error) {
    // eslint-disable-next-line no-console
    console.error("[auth] Firebase error:", error);
  }

  const code =
    typeof error === "object" && error !== null && "code" in error
      ? String((error as { code: unknown }).code)
      : "";

  switch (code) {
    case "auth/invalid-phone-number":
    case "auth/missing-phone-number":
      return "Please enter a valid phone number, including the country code.";
    case "auth/invalid-verification-code":
    case "auth/code-expired":
      return "That code wasn't right. Please request a new one and try again.";
    case "auth/missing-verification-code":
      return "Enter the 6-digit code we sent you.";
    case "auth/quota-exceeded":
      return "We've sent too many codes right now. Please try again later.";
    case "auth/captcha-check-failed":
    case "auth/invalid-app-credential":
    case "auth/missing-app-credential":
      return "We couldn't verify the request. Please refresh and try again.";
    case "auth/too-many-requests":
      return "Too many attempts. Please wait a moment and try again.";
    case "auth/network-request-failed":
      return "We couldn't reach the server. Check your connection and try again.";
    case "auth/operation-not-allowed":
      return "Phone sign-in is not enabled. Please contact support.";
    case "auth/billing-not-enabled":
      return "Phone sign-in is temporarily unavailable. Please contact support.";
    case "auth/internal-error":
    case "auth/argument-error":
      // Catch-all for backend misconfiguration; include the code so the user
      // can read it back to support and we can pinpoint the cause.
      return `We couldn't send the code (${code || "internal-error"}). Please try again or contact support.`;
    case "permission-denied":
      return "We couldn't save your profile. Please try again or contact support.";
    default:
      // Last-resort: surface the raw code so we never debug blind again.
      return code
        ? `Something went wrong (${code}). Please try again.`
        : "Something went wrong. Please try again.";
  }
}
